import {ErrorBox} from '@/components/commons/error-box/ErrorBox'
import {NoPermissionBox} from '@/components/commons/no-permission-box/NoPermissionBox'
import {ButtonLink} from '@/components/ui/button-link/ButtonLink'
import {Container} from '@/components/ui/container/Container'
import {DetailsPageWrapper} from '@/components/ui/details-atoms/DetailsAtoms'
import {AlertCircleIcon, ArrowLeftIcon} from '@/components/ui/icon/Icon'
import {PageCardWrapper} from '@/components/ui/page-atoms/PageAtoms'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {usePermissions} from '@/features/auth/store/store'
import {EditMedicalReportForm} from '@/features/medical-reports/components/edit-medical-report-form/EditMedicalReportForm'
import {useGetMedicalReport} from '@/features/medical-reports/queries/useGetMedicalReport'
import {PatientDetailsMedicalRecordUrlParams} from '@/features/medical-reports/types'
import {useGetPlannedAccesses} from '@/features/planned-accesses/queries/useGetPlannedAccesses'
import {useURLParsedParams} from '@/hooks/useURLParsedParams'
import {useTranslation} from '@/translations/i18n'
import {routes} from '@/utilities/constants/routes'
import {AxiosError} from 'axios'
import dayjs from 'dayjs'
import {Helmet} from 'react-helmet'
import {generatePath} from 'react-router-dom'

export const EditMedicalReport = () => {
    const {t} = useTranslation()
    const permissions = usePermissions()
    const urlParams = useURLParsedParams(PatientDetailsMedicalRecordUrlParams)
    const medicalReportQuery = useGetMedicalReport({
        urlParams: {medicalReportId: urlParams.success ? (urlParams.data?.medicalReportId ?? '') : ''},
        params: {responseType: 'extended'}
    })

    const plannedAccesses = useGetPlannedAccesses({
        urlParams: {
            projectId: medicalReportQuery.data?.project?.id ?? ''
        },
        options: {
            enabled: !!medicalReportQuery.data?.project?.id,
            retry: false
        }
    })

    // renders - Don't add planned accesses loading state here, it will cause an infinite re-render of the form
    if (medicalReportQuery.isPending) {
        return (
            <DetailsPageWrapper isSuccess={false}>
                <Spinner size={32} style={{placeSelf: 'center'}} />
            </DetailsPageWrapper>
        )
    }

    if (
        !urlParams.success ||
        (medicalReportQuery.error instanceof AxiosError && medicalReportQuery.error.response?.status == 404)
    ) {
        return (
            <DetailsPageWrapper isSuccess={false}>
                <ErrorBox icon={<AlertCircleIcon />} title={t('errors:invalid_link')} />
            </DetailsPageWrapper>
        )
    }

    if (medicalReportQuery.error) {
        return (
            <DetailsPageWrapper isSuccess={false}>
                <ErrorBox icon={<AlertCircleIcon />} title={t('errors:default')} />
            </DetailsPageWrapper>
        )
    }

    if (!permissions?.medicalRecords.write) {
        return <NoPermissionBox />
    }

    const canEdit = !!(
        medicalReportQuery.data.status == 'draft' ||
        (medicalReportQuery.data?.status == 'signed' &&
            dayjs().diff(dayjs(medicalReportQuery.data?.signedAt), 'days') <= 30)
    )

    return (
        <>
            <Helmet title={t('medical_records:edit_meta_title')} />
            {canEdit ? (
                <PageCardWrapper>
                    <EditMedicalReportForm
                        medicalReport={medicalReportQuery.data}
                        initialPlannedAccesses={plannedAccesses.data}
                    />
                </PageCardWrapper>
            ) : (
                <Container fullHeight>
                    <ErrorBox
                        icon={<AlertCircleIcon />}
                        title={t('medical_records:edit_report_staled')}
                        cta={
                            <ButtonLink
                                size="xl"
                                variant="secondary"
                                to={generatePath(routes.PATIENT_DETAILS_MEDICAL_RECORDS.handle.path, {
                                    id: urlParams.data?.id ?? ''
                                })}
                            >
                                <ArrowLeftIcon />
                                {t('global_search:medical_record')}
                            </ButtonLink>
                        }
                    />
                </Container>
            )}
        </>
    )
}
